import { DestroyRef, inject, Injectable } from '@angular/core';
import { SyncService } from '../sync/sync.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { BehaviorSubject, Observable, switchMap } from 'rxjs';
import { IStatusResponse } from 'matrix-js-sdk/lib/@types/requests';
import { MatrixService } from '../matrix/matrix.service';
import { Settings } from '../settings/settings.service';
import { AccountService } from '../account/account.service';
import { MatrixClient, SetPresence } from 'matrix-js-sdk';

@Injectable({ providedIn: 'root' })
/**
 * Presence-Service
 * (Alter Code, 1:1 aus RoomService ausgelagert.)
 * TODO: Richtig implementieren (Implementierung geradeziehen), wenn Zeit dafür
 * Begründung: AFO?
 */
export class PresenceService {
  private readonly matrix = inject(MatrixService);
  private readonly syncService = inject(SyncService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly accountService = inject(AccountService);
  private readonly cache = new Map<string, BehaviorSubject<PresenceStatus>>();

  constructor() {
    this.syncService.presence$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(async userPresence => {
      const presence = await this.matrix.client.getPresence(userPresence.userId);
      this.update(userPresence.userId, presence.presence as PresenceStatus);
    });

    this.matrix.updatePresence$.pipe(switchMap(() => this.ensurePresence())).subscribe();
  }

  getPresence(userId: string | undefined): Observable<PresenceStatus> {
    if (!userId) {
      throw new Error('userId was undefined when calling getPresence');
    }
    if (!this.has(userId)) {
      this.matrix.client.getPresence(userId).then(presence => {
        const presenceStatus = this.mapMatrixPresence(presence);
        this.update(userId, presenceStatus);
      });
    }
    return this.get$(userId);
  }

  async updateCurrentUserPresence(): Promise<void> {
    const client = this.matrix.client;
    const presencePrivacy = Settings.getInstance().settingsConfigurations.presencePrivacy;
    const status: PresenceStatus = presencePrivacy ? 'online' : 'offline';
    await this.updateCurrentUserPresenceWithRetryOnce(client, status);
    this.update(this.accountService.getMyMxId(), status);
  }

  private get$(userId: string): Observable<PresenceStatus> {
    return this.getOrCreateSubject(userId).asObservable();
  }

  private update(userId: string, status: PresenceStatus): void {
    this.getOrCreateSubject(userId).next(status);
  }

  private has(userId: string): boolean {
    return this.cache.has(userId);
  }

  private async ensurePresence(): Promise<void> {
    const myMxId = this.accountService.getMyMxId();
    const presenceResponse = await this.matrix.client.getPresence(myMxId);

    const presenceBySettings = Settings.getInstance().settingsConfigurations.presencePrivacy
      ? SetPresence.Online
      : SetPresence.Offline;

    if (presenceBySettings === presenceResponse.presence) {
      console.debug('[Presence] Keine Änderung nötig:', presenceBySettings);
      return;
    }

    await this.updateCurrentUserPresence();
  }

  private async updateCurrentUserPresenceWithRetryOnce(client: MatrixClient, presence: PresenceStatus): Promise<void> {
    try {
      await client.setPresence({ presence });
      console.debug('[Presence] gespeichert:', presence);
    } catch (error: unknown) {
      console.error('[Presence] Fehler:', error);
      await new Promise(resolve => setTimeout(resolve, 2000));
      try {
        await client.setPresence({ presence });
      } catch (retryError) {
        console.error('[Presence] Retry fehlgeschlagen:', retryError);
      }
    }
  }

  private getOrCreateSubject(userId: string): BehaviorSubject<PresenceStatus> {
    if (!this.cache.has(userId)) {
      this.cache.set(userId, new BehaviorSubject<PresenceStatus>('offline'));
    }
    return this.cache.get(userId)!;
  }

  private mapMatrixPresence(response: IStatusResponse): PresenceStatus {
    return response.presence === 'online' ? 'online' : 'offline';
  }
}

export type PresenceStatus = 'offline' | 'online';
